import opencascade from 'replicad-opencascadejs/src/replicad_single.js';
import opencascadeWasm from 'replicad-opencascadejs/src/replicad_single.wasm?url';
import { setOC } from 'replicad';
import * as replicad from 'replicad';
import { expose } from 'comlink';
import * as decorateModule from './replicad-decorate.js';

function getWorkerQueryParam(key) {
  try {
    return new URL(self.location.href).searchParams.get(key) || '';
  } catch (error) {
    return '';
  }
}

function getRuntimeOpenCascadeWasmUrls() {
  const urls = [
    getWorkerQueryParam('replicadWasmUrl'),
    getWorkerQueryParam('replicadLocalWasmUrl'),
    opencascadeWasm
  ].filter(Boolean);

  return [...new Set(urls)];
}

async function loadOpenCascadeFromWasmUrls(wasmUrls) {
  let lastError = null;

  for (const wasmUrl of wasmUrls) {
    try {
      return await opencascade({
        locateFile: () => wasmUrl,
      });
    } catch (error) {
      lastError = error;
      console.warn('[Scene Configurator] Replicad OpenCascade WASM load failed, trying fallback if available:', wasmUrl, error);
    }
  }

  throw lastError || new Error('Replicad OpenCascade WASM failed to load.');
}

// Initialize OpenCascade
let loaded = false;
const init = async () => {
  if (loaded) return Promise.resolve(true);

  const OC = await loadOpenCascadeFromWasmUrls(getRuntimeOpenCascadeWasmUrls());

  loaded = true;
  setOC(OC);

  return true;
};
const started = init();
const persistentMeshTemplateCache = new Map();
const persistentMeshTemplateBuilds = new Map();
const persistentMeshCacheName = 'scene-configurator-mesh-template-cache-v1';
const persistentMeshCacheMaxAgeMs = 24 * 60 * 60 * 1000;
const cacheRequestBasePath = '/__scene_configurator_cache__';

function buildCacheRequest(cacheName, key) {
  const normalizedKey = typeof key === 'string' && key ? key : 'default';
  const requestUrl = new URL(
    `${cacheRequestBasePath}/${encodeURIComponent(cacheName)}/${encodeURIComponent(normalizedKey)}`,
    self.location.origin
  );

  return new Request(requestUrl.toString(), {
    method: 'GET'
  });
}

function shouldPersistMeshTemplate(part = {}) {
  return Boolean(part?.meshTemplateKey) && (
    part.persistMeshTemplate === true ||
    part.meshTemplatePersistence === 'persistent'
  );
}

function getMeshTemplatePrefix(meshTemplateKey) {
  return String(meshTemplateKey || 'none').split('::')[0] || 'none';
}

function createWorkerTimingBuckets() {
  return {
    initWait: 0,
    setupGlobals: 0,
    codeWrap: 0,
    cadFunction: 0,
    instantHandlerExtraction: 0,
    resultNormalization: 0,
    meshCacheMemory: 0,
    meshCacheRead: 0,
    meshCacheInFlightWait: 0,
    meshRaw: 0,
    meshNoTemplateRaw: 0,
    meshCacheWrite: 0,
    meshTransform: 0,
    meshMetadata: 0
  };
}

function addWorkerTiming(timings, key, startTime) {
  timings[key] = (timings[key] || 0) + (performance.now() - startTime);
}

function formatWorkerTiming(value) {
  return Number(value || 0).toFixed(2);
}

function getCadPhaseTimings() {
  const phases = globalThis.__sceneConfiguratorCadPhaseTimings;
  if (!Array.isArray(phases)) {
    return [];
  }

  return phases
    .filter((phase) => phase && typeof phase.name === 'string')
    .map((phase) => ({
      name: phase.name,
      duration: Number(phase.duration || 0),
      count: Number.isFinite(Number(phase.count)) ? Number(phase.count) : undefined
    }));
}

function incrementCount(counts, key) {
  const normalizedKey = String(key || 'none');
  counts[normalizedKey] = (counts[normalizedKey] || 0) + 1;
}

function getPartFamily(part = {}) {
  const family = String(part.diagnosticFamily || part.partFamily || '').trim();
  return family || 'other';
}

function countLineSegments(edges) {
  if (!edges?.lines) {
    return 0;
  }

  return Math.floor(edges.lines.length / 6);
}

function countFaceVertices(faces) {
  if (!faces?.vertices) {
    return 0;
  }

  return Math.floor(faces.vertices.length / 3);
}

function createModelDiagnostics(parts = [], meshes = [], meshTemplateStats = {}) {
  const meshTemplateKeys = new Set();
  const persistentEligibleTemplateKeys = new Set();
  const memoryOnlyTemplateKeys = new Set();
  const materialVariants = new Set();
  const textureVariants = new Set();
  const meshTemplatePrefixUsage = {};
  const persistentEligiblePrefixUsage = {};
  const memoryOnlyPrefixUsage = {};
  const groupUsage = {};
  const edgeTagUsage = {};
  const familyCounts = {};

  parts.forEach((part = {}) => {
    if (part.meshTemplateKey) {
      meshTemplateKeys.add(part.meshTemplateKey);
      const templatePrefix = getMeshTemplatePrefix(part.meshTemplateKey);
      incrementCount(meshTemplatePrefixUsage, templatePrefix);
      if (shouldPersistMeshTemplate(part)) {
        persistentEligibleTemplateKeys.add(part.meshTemplateKey);
        incrementCount(persistentEligiblePrefixUsage, templatePrefix);
      } else {
        memoryOnlyTemplateKeys.add(part.meshTemplateKey);
        incrementCount(memoryOnlyPrefixUsage, templatePrefix);
      }
    }

    incrementCount(groupUsage, part.group || 'none');
    incrementCount(edgeTagUsage, String(part.edgeTag || 'lines').toLowerCase());
    familyCounts[getPartFamily(part)] += 1;
    materialVariants.add(JSON.stringify({
      color: part.color || null,
      materialType: part.materialType || 'basic',
      materialProps: part.materialProps || null
    }));
    textureVariants.add(JSON.stringify({
      texture: part.texture || null,
      textureSettings: part.textureSettings || null,
      needsUv: part.needsUv === true
    }));
  });

  return {
    partCount: parts.length,
    meshCount: meshes.length,
    meshTemplateKeyCount: meshTemplateKeys.size,
    meshTemplatePartCount: parts.filter((part) => Boolean(part?.meshTemplateKey)).length,
    materialVariantCount: materialVariants.size,
    textureVariantCount: textureVariants.size,
    groupUsage,
    edgeTagUsage,
    familyCounts,
    meshTemplatePersistence: {
      eligibleTemplateKeyCount: persistentEligibleTemplateKeys.size,
      eligiblePartCount: parts.filter((part) => part?.meshTemplateKey && shouldPersistMeshTemplate(part)).length,
      memoryOnlyTemplateKeyCount: memoryOnlyTemplateKeys.size,
      memoryOnlyPartCount: parts.filter((part) => part?.meshTemplateKey && !shouldPersistMeshTemplate(part)).length,
      prefixUsage: meshTemplatePrefixUsage,
      eligiblePrefixUsage: persistentEligiblePrefixUsage,
      memoryOnlyPrefixUsage
    },
    meshTemplateCache: {
      memoryHits: meshTemplateStats.memoryHits || 0,
      persistentHits: meshTemplateStats.persistentHits || 0,
      inFlightHits: meshTemplateStats.inFlightHits || 0,
      misses: meshTemplateStats.misses || 0,
      persistentWrites: meshTemplateStats.persistentWrites || 0
    },
    geometry: {
      faceVertexCount: meshes.reduce((total, mesh) => total + countFaceVertices(mesh?.faces), 0),
      edgeLineSegmentCount: meshes.reduce((total, mesh) => total + countLineSegments(mesh?.edges), 0),
      meshesWithEdges: meshes.filter((mesh) => countLineSegments(mesh?.edges) > 0).length
    }
  };
}

function buildPersistentMeshCacheKey(cadCodeHash, meshTemplateKey) {
  return `${persistentMeshCacheName}:${cadCodeHash || 'no-code-hash'}:${meshTemplateKey}`;
}

function serializeMeshPart(part) {
  if (!part) {
    return part;
  }

  return {
    ...part,
    vertices: part.vertices ? Array.from(part.vertices) : part.vertices,
    normals: part.normals ? Array.from(part.normals) : part.normals,
    lines: part.lines ? Array.from(part.lines) : part.lines
  };
}

async function readPersistentMeshTemplate(cadCodeHash, meshTemplateKey) {
  try {
    const cache = await caches.open(persistentMeshCacheName);
    const cacheRequest = buildCacheRequest(
      persistentMeshCacheName,
      buildPersistentMeshCacheKey(cadCodeHash, meshTemplateKey)
    );
    const response = await cache.match(cacheRequest);
    if (!response) {
      return null;
    }

    const payload = await response.json();
    if (!payload?.savedAt || (Date.now() - payload.savedAt) > persistentMeshCacheMaxAgeMs) {
      await cache.delete(cacheRequest);
      return null;
    }

    return payload.mesh || null;
  } catch (error) {
    console.warn('[Worker] Failed to read persistent mesh template cache:', error);
    return null;
  }
}

async function writePersistentMeshTemplate(cadCodeHash, meshTemplateKey, mesh) {
  try {
    const cache = await caches.open(persistentMeshCacheName);
    const cacheRequest = buildCacheRequest(
      persistentMeshCacheName,
      buildPersistentMeshCacheKey(cadCodeHash, meshTemplateKey)
    );
    await cache.put(
      cacheRequest,
      new Response(JSON.stringify({
        savedAt: Date.now(),
        mesh: {
          faces: serializeMeshPart(mesh.faces),
          edges: serializeMeshPart(mesh.edges)
        }
      }), {
        headers: { 'Content-Type': 'application/json' }
      })
    );
  } catch (error) {
    console.warn('[Worker] Failed to write persistent mesh template cache:', error);
  }
}

// Execute user code and create mesh
function executeCode(code, meshConfig = { tolerance: 0.01, angularTolerance: 30 }) {
  const workerCallStart = performance.now();
  return started.then(async () => {
    const startTime = performance.now();
    const workerTimings = createWorkerTimingBuckets();
    workerTimings.initWait = startTime - workerCallStart;
    const meshTemplateCache = persistentMeshTemplateCache;
    const meshTemplateBuilds = persistentMeshTemplateBuilds;
    const cadCodeHash = meshConfig?.cadCodeHash || 'no-code-hash';
    globalThis.__sceneConfiguratorCadCodeHash = cadCodeHash;
    const cloneNumericArray = (values) => {
      if (!values) {
        return values;
      }
      return ArrayBuffer.isView(values) ? values.slice() : values.slice();
    };
    const normalizeMeshTransform = (transform) => {
      const safeTransform = transform && typeof transform === 'object' ? transform : {};
      return {
        rotateY180: safeTransform.rotateY180 === true,
        rotateZ: Number.isFinite(Number(safeTransform.rotateZ)) ? Number(safeTransform.rotateZ) : 0,
        translate: Array.isArray(safeTransform.translate) ? safeTransform.translate : [0, 0, 0],
        scale: Array.isArray(safeTransform.scale)
          ? safeTransform.scale
          : typeof safeTransform.scale === 'number'
            ? [safeTransform.scale, safeTransform.scale, safeTransform.scale]
            : [1, 1, 1]
      };
    };
    const transformTriples = (values, transform, rotateOnly = false) => {
      if (!values) {
        return values;
      }
      const output = cloneNumericArray(values);
      const [sx, sy, sz] = transform.scale;
      const [tx, ty, tz] = transform.translate;
      for (let index = 0; index + 2 < output.length; index += 3) {
        let x = output[index];
        let y = output[index + 1];
        let z = output[index + 2];
        x *= sx;
        y *= sy;
        z *= sz;
        if (transform.rotateZ) {
          const angle = transform.rotateZ * Math.PI / 180;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const rotatedX = (x * cos) - (y * sin);
          const rotatedY = (x * sin) + (y * cos);
          x = rotatedX;
          y = rotatedY;
        }
        if (transform.rotateY180) {
          x = -x;
          z = -z;
        }
        if (!rotateOnly) {
          x += tx;
          y += ty;
          z += tz;
        }
        output[index] = x;
        output[index + 1] = y;
        output[index + 2] = z;
      }
      return output;
    };
    const transformFaceMesh = (faces, meshTransform) => {
      if (!faces) {
        return faces;
      }
      const transform = normalizeMeshTransform(meshTransform);
      if (!transform.rotateY180 && !transform.rotateZ && transform.translate.every((value) => value === 0)) {
        return faces;
      }
      return {
        ...faces,
        vertices: transformTriples(faces.vertices, transform),
        normals: faces.normals ? transformTriples(faces.normals, transform, true) : faces.normals
      };
    };
    const transformEdgeMesh = (edges, meshTransform) => {
      if (!edges) {
        return edges;
      }
      const transform = normalizeMeshTransform(meshTransform);
      if (!transform.rotateY180 && !transform.rotateZ && transform.translate.every((value) => value === 0)) {
        return edges;
      }
      return {
        ...edges,
        lines: transformTriples(edges.lines, transform)
      };
    };
    
    try {
      const codeWrapStart = performance.now();
      // Create a function from the code with replicad available
      let wrappedCode = code.trim();
      
      // Remove any replicad destructuring from user code to avoid conflicts
      wrappedCode = wrappedCode.replace(/^\s*const\s*\{\s*[^}]+\}\s*=\s*replicad\s*;?\s*$/gm, '');
      
      // Check if the code defines a main() function
      const hasMainFunction = /function\s+main\s*\(/.test(wrappedCode) || /const\s+main\s*=/.test(wrappedCode);
      
      addWorkerTiming(workerTimings, 'codeWrap', codeWrapStart);

      const setupGlobalsStart = performance.now();
      // Make replicad exports available to decorateModule by setting them globally in worker scope
      Object.assign(globalThis, {
        drawFaceOutline: replicad.drawFaceOutline,
        getOC: replicad.getOC,
        GCWithScope: replicad.GCWithScope,
        loadFont: replicad.loadFont,
        drawText: replicad.drawText,
        drawPolysides: replicad.drawPolysides,
        Drawing: replicad.Drawing,
        drawRoundedRectangle: replicad.drawRoundedRectangle,
        fuseBlueprints: replicad.fuseBlueprints,
        BlueprintSketcher: replicad.BlueprintSketcher,
        organiseBlueprints: replicad.organiseBlueprints,
        draw: replicad.draw,
        importSTEP: replicad.importSTEP
      });
      addWorkerTiming(workerTimings, 'setupGlobals', setupGlobalsStart);
      
      let func;
      const functionCompileStart = performance.now();
      if (hasMainFunction) {
        // If there's a main function, execute the code and then call main()
        // Extract custom parameters (excluding tolerance/angularTolerance which are for meshing)
        const customParams = { ...meshConfig };
        delete customParams.tolerance;
        delete customParams.angularTolerance;
        
        func = new Function('replicad', 'decorateModule', 'customParams', `
          'use strict';
          const { draw, drawCircle, drawRectangle, drawFaceOutline, getOC, GCWithScope, loadFont, drawText, drawPolysides, Drawing, drawRoundedRectangle, fuseBlueprints, BlueprintSketcher, organiseBlueprints, importSTEP } = replicad;
          const { addSVG, drawSVG } = decorateModule;
          ${wrappedCode}
          // Return a promise that resolves when main() completes (handles async main functions)
          return (async () => {
            return (typeof main === 'function') ? await main(replicad, customParams) : null;
          })();
        `);
      } else {
        // Otherwise, wrap the code in a function that returns the result
        func = new Function('replicad', 'decorateModule', `
          'use strict';
          const { draw, drawCircle, drawRectangle, drawFaceOutline, getOC, GCWithScope, loadFont, drawText, drawPolysides, Drawing, drawRoundedRectangle, fuseBlueprints, BlueprintSketcher, organiseBlueprints, importSTEP } = replicad;
          const { addSVG, drawSVG } = decorateModule;
          ${wrappedCode}
        `);
      }
      addWorkerTiming(workerTimings, 'codeWrap', functionCompileStart);
      
      const executeStart = performance.now();
      
      // Execute the code
      // Extract custom parameters for main function
      const customParams = { ...meshConfig };
      delete customParams.tolerance;
      delete customParams.angularTolerance;
      globalThis.__sceneConfiguratorCadPhaseTimings = [];
      
      const cadFunctionStart = performance.now();
      let result = await func(replicad, decorateModule, customParams);
      addWorkerTiming(workerTimings, 'cadFunction', cadFunctionStart);
      
      // Try to extract instantUpdateHandlers if defined in the code
      // This allows CAD sketches to define instant update handlers
      let instantUpdateHandlers = null;
      const instantHandlerStart = performance.now();
      try {
        // Check if instantUpdateHandlers was defined during code execution
        // Look for: const instantUpdateHandlers = { ... };
        // Need to match balanced braces to capture entire object
        const startMatch = wrappedCode.match(/const\s+instantUpdateHandlers\s*=\s*\{/);
        if (startMatch) {
          let startIdx = wrappedCode.indexOf(startMatch[0]) + startMatch[0].length - 1; // Position of opening {
          let endIdx = startIdx;
          let braceCount = 0;
          
          // Find matching closing brace
          for (let i = startIdx; i < wrappedCode.length; i++) {
            if (wrappedCode[i] === '{') braceCount++;
            if (wrappedCode[i] === '}') braceCount--;
            if (braceCount === 0) {
              endIdx = i;
              break;
            }
          }
          
          const handlersCode = wrappedCode.substring(startIdx, endIdx + 1);
          
          // Evaluate to get the object
          const handlersFunc = new Function(`return ${handlersCode};`);
          const handlers = handlersFunc();
          
          // Serialize handlers as function strings
          instantUpdateHandlers = {};
          for (const key in handlers) {
            instantUpdateHandlers[key] = handlers[key].toString();
          }
        }
      } catch (e) {
        console.warn('[Worker] Failed to extract instant update handlers:', e);
        console.warn('[Worker] Error details:', e.message, e.stack);
      }
      addWorkerTiming(workerTimings, 'instantHandlerExtraction', instantHandlerStart);
      
      const resultNormalizationStart = performance.now();
      // Handle results that return objects with a shape property (common pattern)
      if (result && result.shape && typeof result.shape.mesh === 'function') {
        result = result.shape;
      }
      
      // Check if result is a valid shape or array of shapes
      if (!result) {
        throw new Error('Code must return a value. Make sure your code returns a shape or array of shapes.');
      }
      
      // Check if result is an array of {shape, name, color, texture} objects
      const isShapeArray = Array.isArray(result) && 
                          result.length > 0 && 
                          result[0].shape && 
                          typeof result[0].shape.mesh === 'function';
      addWorkerTiming(workerTimings, 'resultNormalization', resultNormalizationStart);
      
      const meshStart = performance.now();
      
      let meshData;
      
      if (isShapeArray) {
        const meshTemplateStats = {
          memoryHits: 0,
          persistentHits: 0,
          inFlightHits: 0,
          misses: 0,
          persistentWrites: 0
        };
        // Handle array of shapes with metadata
        const meshes = await Promise.all(result.map(async (item, index) => {
          const edgeTag = String(item.edgeTag || 'lines').toLowerCase();
          const meshTemplateKey = item.meshTemplateKey || null;
          const meshTemplateVariantKey = meshTemplateKey ? `${meshTemplateKey}::edge-${edgeTag}` : null;
          const scopedMeshTemplateKey = meshTemplateVariantKey ? `${cadCodeHash}::${meshTemplateVariantKey}` : null;
          const meshTransform = item.meshTransform || null;
          let faces;
          let edges;

          if (meshTemplateKey) {
            const memoryCacheStart = performance.now();
            let cachedMesh = meshTemplateCache.get(scopedMeshTemplateKey);
            addWorkerTiming(workerTimings, 'meshCacheMemory', memoryCacheStart);
            if (cachedMesh) {
              meshTemplateStats.memoryHits += 1;
            }
            if (!cachedMesh) {
              const existingBuild = meshTemplateBuilds.get(scopedMeshTemplateKey);
              if (existingBuild) {
                meshTemplateStats.inFlightHits += 1;
                const inFlightStart = performance.now();
                cachedMesh = await existingBuild;
                addWorkerTiming(workerTimings, 'meshCacheInFlightWait', inFlightStart);
              } else {
                const buildTemplateMesh = (async () => {
                  let templateMesh = null;
                  if (shouldPersistMeshTemplate(item)) {
                    const persistentReadStart = performance.now();
                    templateMesh = await readPersistentMeshTemplate(cadCodeHash, meshTemplateVariantKey);
                    addWorkerTiming(workerTimings, 'meshCacheRead', persistentReadStart);
                    if (templateMesh) {
                      meshTemplateStats.persistentHits += 1;
                    }
                  }
                  if (!templateMesh) {
                    meshTemplateStats.misses += 1;
                    const rawMeshStart = performance.now();
                    templateMesh = {
                      faces: item.shape.mesh(meshConfig),
                      edges: edgeTag === 'lines' ? item.shape.meshEdges(meshConfig) : undefined
                    };
                    addWorkerTiming(workerTimings, 'meshRaw', rawMeshStart);
                    if (shouldPersistMeshTemplate(item)) {
                      const persistentWriteStart = performance.now();
                      await writePersistentMeshTemplate(cadCodeHash, meshTemplateVariantKey, templateMesh);
                      addWorkerTiming(workerTimings, 'meshCacheWrite', persistentWriteStart);
                      meshTemplateStats.persistentWrites += 1;
                    }
                  }

                  return templateMesh;
                })();

                meshTemplateBuilds.set(scopedMeshTemplateKey, buildTemplateMesh);
                try {
                  cachedMesh = await buildTemplateMesh;
                } finally {
                  meshTemplateBuilds.delete(scopedMeshTemplateKey);
                }
              }
              meshTemplateCache.set(scopedMeshTemplateKey, cachedMesh);
            }

            const meshTransformStart = performance.now();
            faces = transformFaceMesh(cachedMesh.faces, meshTransform);
            edges = edgeTag === 'lines'
              ? transformEdgeMesh(cachedMesh.edges, meshTransform)
              : undefined;
            addWorkerTiming(workerTimings, 'meshTransform', meshTransformStart);
          } else {
            const rawMeshStart = performance.now();
            faces = item.shape.mesh(meshConfig);
            edges = edgeTag === 'lines' ? item.shape.meshEdges(meshConfig) : undefined;
            addWorkerTiming(workerTimings, 'meshNoTemplateRaw', rawMeshStart);
          }

          const meshMetadataStart = performance.now();
          const meshRecord = {
            faces,
            edges,
            name: item.name || `Shape ${index + 1}`,
            color: item.color || '#4a90e2',
            texture: item.texture || null,
            textureSettings: item.textureSettings || null,
            materialType: item.materialType || null,
            materialProps: item.materialProps || null,
            needsUv: item.needsUv === true,
            group: item.group || null,
            partKey: item.partKey || null,
            zoneKey: item.zoneKey || null,
            sectionKey: item.sectionKey || null,
            hotspot: item.hotspot || null,
            cameraViewKey: item.cameraViewKey || null,
            hotspotGroupKey: item.hotspotGroupKey || null,
            hotspotGroup: item.hotspotGroup || null,
            hotspotBindingKeys: item.hotspotBindingKeys || null,
            hotspotChoiceKeys: item.hotspotChoiceKeys || null,
            hotspotHighlightTarget: item.hotspotHighlightTarget || null,
            hotspotClickTarget: item.hotspotClickTarget || null,
            hotspotOptionContribution: item.hotspotOptionContribution || null,
            diagnosticFamily: item.diagnosticFamily || item.partFamily || null,
            edgeTag: item.edgeTag || null,
            seamGroup: item.seamGroup || null,
            seamPolicy: item.seamPolicy || null,
            seamMatchMaterial: item.seamMatchMaterial || null
          };
          addWorkerTiming(workerTimings, 'meshMetadata', meshMetadataStart);
          return meshRecord;
        }));
        
        meshData = {
          success: true,
          meshes: meshes,
          meshConfig,
          instantUpdateHandlers: instantUpdateHandlers,
          modelDiagnostics: createModelDiagnostics(result, meshes, meshTemplateStats),
          timing: {
            totalWithInit: formatWorkerTiming(performance.now() - workerCallStart),
            total: (performance.now() - startTime).toFixed(2),
            execution: (performance.now() - executeStart).toFixed(2),
            meshing: (performance.now() - meshStart).toFixed(2),
            initWait: formatWorkerTiming(workerTimings.initWait),
            setupGlobals: formatWorkerTiming(workerTimings.setupGlobals),
            codeWrap: formatWorkerTiming(workerTimings.codeWrap),
            cadFunction: formatWorkerTiming(workerTimings.cadFunction),
            cadPhases: getCadPhaseTimings(),
            instantHandlerExtraction: formatWorkerTiming(workerTimings.instantHandlerExtraction),
            resultNormalization: formatWorkerTiming(workerTimings.resultNormalization),
            meshCacheMemory: formatWorkerTiming(workerTimings.meshCacheMemory),
            meshCacheRead: formatWorkerTiming(workerTimings.meshCacheRead),
            meshCacheInFlightWait: formatWorkerTiming(workerTimings.meshCacheInFlightWait),
            meshRaw: formatWorkerTiming(workerTimings.meshRaw),
            meshNoTemplateRaw: formatWorkerTiming(workerTimings.meshNoTemplateRaw),
            meshCacheWrite: formatWorkerTiming(workerTimings.meshCacheWrite),
            meshTransform: formatWorkerTiming(workerTimings.meshTransform),
            meshMetadata: formatWorkerTiming(workerTimings.meshMetadata)
          }
        };
      } else {
        // Handle single shape (backward compatibility)
        if (typeof result.mesh !== 'function') {
          throw new Error('Code must return a valid replicad shape. Got: ' + typeof result);
        }

        const rawMeshStart = performance.now();
        const singleMesh = {
          faces: result.mesh(meshConfig),
          edges: result.meshEdges(meshConfig),
        };
        addWorkerTiming(workerTimings, 'meshNoTemplateRaw', rawMeshStart);
        
        meshData = {
          success: true,
          mesh: singleMesh,
          meshConfig,
          instantUpdateHandlers: instantUpdateHandlers,
          modelDiagnostics: createModelDiagnostics([{ name: 'Shape 1' }], [singleMesh]),
          timing: {
            totalWithInit: formatWorkerTiming(performance.now() - workerCallStart),
            total: (performance.now() - startTime).toFixed(2),
            execution: (performance.now() - executeStart).toFixed(2),
            meshing: (performance.now() - meshStart).toFixed(2),
            initWait: formatWorkerTiming(workerTimings.initWait),
            setupGlobals: formatWorkerTiming(workerTimings.setupGlobals),
            codeWrap: formatWorkerTiming(workerTimings.codeWrap),
            cadFunction: formatWorkerTiming(workerTimings.cadFunction),
            cadPhases: getCadPhaseTimings(),
            instantHandlerExtraction: formatWorkerTiming(workerTimings.instantHandlerExtraction),
            resultNormalization: formatWorkerTiming(workerTimings.resultNormalization),
            meshCacheMemory: formatWorkerTiming(workerTimings.meshCacheMemory),
            meshCacheRead: formatWorkerTiming(workerTimings.meshCacheRead),
            meshCacheInFlightWait: formatWorkerTiming(workerTimings.meshCacheInFlightWait),
            meshRaw: formatWorkerTiming(workerTimings.meshRaw),
            meshNoTemplateRaw: formatWorkerTiming(workerTimings.meshNoTemplateRaw),
            meshCacheWrite: formatWorkerTiming(workerTimings.meshCacheWrite),
            meshTransform: formatWorkerTiming(workerTimings.meshTransform),
            meshMetadata: formatWorkerTiming(workerTimings.meshMetadata)
          }
        };
      }
      
      return meshData;
    } catch (error) {
      const errorType = typeof error;
      const errorMessage = error instanceof Error
        ? error.message
        : errorType === 'string'
          ? error
          : errorType === 'number' || errorType === 'boolean'
            ? String(error)
            : 'Worker execution failed';
      const errorStack = error instanceof Error ? error.stack : undefined;
      const errorDetails = error && typeof error === 'object'
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
            cause: error.cause,
            code: error.code,
            data: error.data,
          }
        : {
            value: error,
            type: errorType,
          };
      console.error('[Worker] Error during execution:', error);
      console.error('[Worker] Error stack:', errorStack);
      return {
        success: false,
        error: errorMessage,
        errorStack,
        errorDetails,
      };
    }
  });
}

// Expose functions to main thread
expose({ executeCode });
